import { createClaimFromPolicy, exportClaimDetails, getAllClaims, getClaimById } from "../api/requests";
import { CLAIM_STAGES, UPLOAD_IMAGE_ENDPOINTS } from "../resources/enums";
import useModal from "./modalHook";
import { Claim } from "../models/claim/Claim";
import { useQueryClient } from "@tanstack/react-query";
import { generateNonMotorClaimFromPolicy, getAllNonMotorClaims, getNonMotorClaimByClaimNumber, getNonMotorClaimsStatistics } from "../services/claims";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import useRoute from "./routesHook";
import { TwoStateVerifiedCheck } from "../components/statuses";
import { capitalizeSentence, getFormattedDate, getVehicleProtectionFromKey, removeUnderscoresAndCapitalize, showToast } from "../utils/miscFunctions";
import ClaimStages from "../components/elements/ClaimStages";
import PolicyStatus from "../components/elements/PolicyStatus";
import { useMemo, useState } from "react";
import { ITablePaginationData } from "../components/elements/GTable/GTable";
import { ISelectDropdownItem } from "../components/form-control/SelectDropdown";
import usePolicy from "./policyHook";
import { CheckCircleIcon, EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import { getEnumsFromState, getSelectedCountry } from "../states/slices/resourceSlice";
import { IEnum, ITableFilterItem } from "../resources/interfaces";
import ActionConfirmation from "../components/elements/ActionConfirmation";
import PopOnMe from "../components/elements/poponme";
import UploadClaimDocument from "../views/shared/policies/non-auto/claims/components/UploadClaimsDocs";
import UpdateClaimStageModal from "../views/shared/policies/auto/claims/UpdateClaimStageModal";


type IUseClaim = {
    claim?: Claim
    getTableRowActions?: any
}

export const useClaim = ({ claim, getTableRowActions }: IUseClaim = {}) => {

    const { ModalComponent, updateModalStates, closeModal } = useModal();
    const [claimsPaginationData, setClaimsPaginationData] = useState<ITablePaginationData>();
    const queryClient = useQueryClient();
    const ClaimModal = ModalComponent
    const { claimNumber } = useParams()
    const { getDashboardBase } = useRoute();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams()
    const isMotorClaim = searchParams.get("type") === "motor"
    const claimType = isMotorClaim ? "motor" : "non-motor"
    const { PolicyTypesBadge } = usePolicy()
    const selectedCountry = useSelector(getSelectedCountry);
    const countryId = selectedCountry?.id ?? 1
    const enums = useSelector(getEnumsFromState);
    const invalidategetAllClaimsList = () => {
        queryClient.invalidateQueries({ queryKey: ['getAllClaimsList'] });
    }

    const invalidateClaim = () => {
        queryClient.invalidateQueries({ queryKey: [isMotorClaim ? "fetchClaim" : 'fetchNonAutoClaimByClaimNumber'] });
    }

    const getAllClaimsList = async (page: number, size: number, searchParam?: string, policyType?: string, stage?: CLAIM_STAGES) => {
        try {
            const response: any = isMotorClaim ? await getAllClaims(page, size, countryId, searchParam, stage) :
                await getAllNonMotorClaims(page, size, searchParam, policyType, stage);
            const _claims = response?.content?.map((claim: any) => new Claim(claim));
            const from = response?.pageable?.offset + 1;
            const to =
                from + response?.size > response?.totalElements
                    ? response?.totalElements
                    : from + response?.size - 1;
            setClaimsPaginationData({
                per_page: response?.size,
                total: response?.totalElements,
                totalPages: response?.totalPages,
                from: from,
                to: to
            });
            return _claims;
        } catch (error) { }
    };

    const fetchClaimStatistics = async () => {
        return new Promise<any>((resolve, reject) => {
            getNonMotorClaimsStatistics()
                .then((response: any) => {
                    resolve(response.data);
                })
                .catch((error: Error) => {
                    reject(error);
                });
        });
    };

    const fetchClaim = async (claimNumber: string) => {
        if (!claimNumber) return null;
        return new Promise<Claim>((resolve, reject) => {
            getClaimById(claimNumber)
                .then((response: any) => {
                    resolve(new Claim(response.data));
                })
                .catch((error: Error) => {
                    reject(error);
                });
        });
    };

    const handleUpdateClaimStageToInReview = () => {
        updateModalStates({
            show: true,
            title: "Update claim stage to In Review",
            children: (
                <UpdateClaimStageModal
                    claim={claim}
                    onSubmitted={() => {
                        closeModal();
                        invalidateClaim()
                    }}
                    onCancel={closeModal}
                    stage={CLAIM_STAGES.IN_REVIEW}
                    type={claimType}
                />
            ),
        });
    };

    const handleUpdateClaimStageToSubmitted = () => {
        updateModalStates({
            show: true,
            title: 'Update claim stage to Submitted',
            children: (
                <UpdateClaimStageModal
                    claim={claim}
                    onSubmitted={() => {
                        closeModal();
                        invalidateClaim()
                    }}
                    onCancel={closeModal}
                    stage={CLAIM_STAGES.SUBMITTED}
                    type={claimType}
                />
            ),
        });
    };

    const handleUpdateClaimStageToAcknowledged = () => {
        updateModalStates({
            show: true,
            title: 'Update claim stage to Acknowledged',
            children: (
                <UpdateClaimStageModal
                    claim={claim}
                    onSubmitted={() => {
                        closeModal();
                        invalidateClaim()
                    }}
                    onCancel={closeModal}
                    stage={CLAIM_STAGES.ACKNOWLEDGED}
                    type={claimType}

                />
            ),
        });
    };

    const handleUpdateClaimStageToAssessed = () => {
        updateModalStates({
            show: true,
            title: 'Update claim stage to Assessed',
            children: (
                <UpdateClaimStageModal
                    claim={claim}
                    onSubmitted={() => {
                        closeModal();
                        invalidateClaim()
                    }}
                    onCancel={closeModal}
                    stage={CLAIM_STAGES.ASSESSED}
                    type={claimType}

                />
            ),
        });
    };

    const handleUpdateClaimStageToAdjusted = () => {
        updateModalStates({
            show: true,
            title: 'Update claim stage to adjusted',
            children: (
                <UpdateClaimStageModal
                    claim={claim}
                    onSubmitted={() => {
                        closeModal();
                        invalidateClaim()
                    }}
                    onCancel={closeModal}
                    stage={CLAIM_STAGES.ADJUSTED}
                    type={claimType}

                />
            ),
        });
    };

    const handleUpdateClaimStageToDvIssued = () => {
        updateModalStates({
            show: true,
            title: "Update claim stage to DV issued",
            children: (
                <UpdateClaimStageModal
                    claim={claim}
                    onSubmitted={() => {
                        closeModal();
                        invalidateClaim()
                    }}
                    onCancel={closeModal}
                    stage={CLAIM_STAGES.DV_ISSUED}
                    type={claimType}

                />
            ),
        });
    };

    const handleUpdateClaimStageToDvSigned = () => {
        updateModalStates({
            show: true,
            title: "Update claim stage to DV Signed",
            children: (
                <UpdateClaimStageModal
                    claim={claim}
                    onSubmitted={() => {
                        closeModal();
                        invalidateClaim()
                    }}
                    onCancel={closeModal}
                    stage={CLAIM_STAGES.DV_SIGNED}
                    type={claimType}
                />
            ),
        });
    };

    const handleUpdateClaimStageToPaid = () => {
        updateModalStates({
            show: true,
            children: (
                <UpdateClaimStageModal
                    claim={claim}
                    onSubmitted={() => {
                        closeModal();
                        invalidateClaim()
                    }}
                    onCancel={closeModal}
                    stage={CLAIM_STAGES.PAID}
                    type={claimType}

                />
            )
        });
    };

    function getInitFilters(searchParams: URLSearchParams): CLAIM_STAGES | 'All' {
        switch (searchParams.get('filter')?.toLowerCase()) {
            case 'new':
                return CLAIM_STAGES.NEW;

            case 'in_review':
                return CLAIM_STAGES.IN_REVIEW;

            case 'submitted':
                return CLAIM_STAGES.SUBMITTED;

            case 'acknowledged':
                return CLAIM_STAGES.ACKNOWLEDGED;

            case 'assessed':
                return CLAIM_STAGES.ASSESSED;

            case 'adjusted':
                return CLAIM_STAGES.ADJUSTED;

            case 'dv_issued':
                return CLAIM_STAGES.DV_ISSUED;

            case 'dv_signed':
                return CLAIM_STAGES.DV_SIGNED;

            case 'paid':
                return CLAIM_STAGES.PAID;

            default:
                return 'All';
        }
    }

    const exportClaim = async () => {
        try {
            if (claim?.claimNumber) {
                const response: any = await exportClaimDetails(claim?.claimNumber);
                window.open(response?.url, '_blank');
            }
        } catch (error: any) {
        }
    };

    const CreateClaimModalFirstChild = () => {
        return (
            <div className="py-6 px-4 md:px-6 w-full flex flex-col items-center text-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-main mb-1"></div>
                <p className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Initiating claim</p>
                <p className="text-sm text-gray-600 max-w-sm">
                    Please wait while we initiate your claims process...
                </p>
            </div>
        )
    }

    const CreateClaimModalSecondChild = () => {
        return (
            <div className="py-6 px-4 md:px-6 w-full flex flex-col items-center text-center space-y-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-50 mb-1">
                    <CheckCircleIcon className="h-7 w-7 text-green-600" />
                </div>
                <p className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Claim initiated successfully</p>
                <p className="text-sm text-gray-600 max-w-sm">
                    Your claims has been initiated successfully.
                </p>
            </div>
        )
    }

    const createClaim = async (policyNumber: string, isMotorClaim: boolean) => {
        if (!policyNumber) return;

        // Step 1: Show confirmation modal
        updateModalStates({
            show: true,
            title: 'Initiate Claim Process',
            size: 'sm',
            children: (
                <ActionConfirmation
                    action="initiate claim process for"
                    resourceName={policyNumber}
                    resourceType="policy"
                    onConfirm={async () => {
                        // Step 2: Show loading modal
                        updateModalStates({
                            show: true,
                            title: 'Claim Initiation',
                            size: 'sm',
                            children: <CreateClaimModalFirstChild />,
                        });

                        try {
                            // Step 3: Call API to create claim
                            const response: any = isMotorClaim ? await createClaimFromPolicy(policyNumber) : await generateNonMotorClaimFromPolicy(policyNumber);
                            if (response) {
                                // Step 4: Show success modal and auto-navigate
                                updateModalStates({
                                    show: true,
                                    size: 'sm',
                                    children: <CreateClaimModalSecondChild />,
                                });

                                // Auto-navigate after 2 seconds
                                setTimeout(() => {
                                    closeModal();
                                    navigate(`${getDashboardBase()}/claims/${response?.claimNumber}/edit?type=${isMotorClaim ? 'motor' : 'non-motor'}`);
                                }, 2000);
                            }
                        } catch (error: any) {
                            closeModal();
                            showToast.error(error.description ?? 'Failed to create claim from policy');
                        }
                    }} onClose={() => {
                        closeModal();
                    }}
                />
            ),
        });
    };

    const columns = useMemo(
        () => [
            {
                Header: 'M',
                columns: [
                    {
                        Header: 'Applicant',
                        accessor: 'customerFullName',
                        Cell: (row: any) => {
                            return (
                                <Link to={`${getDashboardBase()}/customers/${row.row.original?.customer?.id}`} >
                                    <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{capitalizeSentence(row.cell.value) ?? '-'}</span>
                                </Link>
                            )
                        }
                    },
                    {
                        Header: 'Claim Number',
                        accessor: 'claimNumber',
                        Cell: (row: any) => {
                            return (
                                <Link to={`${getDashboardBase()}/claims/${row.cell.value}?type=${isMotorClaim ? 'motor' : 'non-motor'}`} >
                                    <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                                </Link>
                            )
                        }
                    },
                    {
                        Header: 'Policy Number',
                        accessor: 'policyNumber',
                        Cell: (row: any) => {
                            return (
                                <Link to={`${getDashboardBase()}/policies/${row.cell.value}?type=${isMotorClaim ? 'motor' : 'non-motor'}`} >
                                    <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                                </Link>
                            );
                        }
                    },
                    ...(isMotorClaim ? [
                        {
                            Header: 'Protection Type',
                            accessor: 'stage',
                            Cell: (row: any) => {
                                return row.cell.value ? <PolicyStatus
                                    status={getVehicleProtectionFromKey(row.cell.value)?.title ?? ''}

                                    classes="px-2"
                                /> : <span className='text-center'> {'-'}</span>
                            }
                        }
                    ] : [{
                        Header: 'Policy Type',
                        accessor: 'policyType',
                        Cell: (row: any) => {
                            return row.cell.value ? <PolicyTypesBadge policyType={row.cell.value} /> : <span className='text-center'> {'-'}</span>
                        }
                    }
                    ]),
                    ...(isMotorClaim ? [{
                        Header: 'Inception Date',
                        accessor: 'policyInception',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Expiry Date',
                        accessor: 'expiry',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>;
                        }
                    }] : []),
                    {
                        Header: 'Amount/ Cost',
                        accessor: 'claimAmount',
                        Cell: (row: any) => {
                            return <span className='flex items-center'>{row.cell.value ?? '-'}</span>;
                        }
                    },
                    ...(isMotorClaim ? [{
                        Header: 'NCD Effect',
                        accessor: 'ncdEffect',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ?? '-'}</span>;
                        }
                    }] : []),
                    {
                        Header: 'Stage',
                        accessor: 'claimStage',
                        Cell: (row: any) => {
                            return <ClaimStages status={row.cell.value} />;
                        }
                    },
                    {
                        Header: 'Paid',
                        accessor: 'paid',
                        Cell: (row: any) => {
                            return <TwoStateVerifiedCheck verified={row.cell.value} />;
                        }
                    },
                    {
                        Header: 'Created On',
                        accessor: 'createdOn',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '- '}</span>;
                        }
                    },
                    {
                        Header: 'Updated On',
                        accessor: 'updatedOn',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>;
                        }
                    },
                    ...(getTableRowActions ? [{
                        id: 'actions',
                        Cell: (row: any) => (
                            <div className="cursor-pointer flex justify-center">
                                {getTableRowActions(row.row.original).length > 0 && (
                                    <PopOnMe actions={getTableRowActions(row.row.original)}>
                                        <EllipsisVerticalIcon className="h-4 w-4" />
                                    </PopOnMe>
                                )}
                            </div>)
                    }] : [])
                ]
            }
        ],
        []
    );

    const CLAIM_TABLE_FILTER_OPTIONS: ISelectDropdownItem[] = [
        {
            name: 'All',
            value: 'All'
        },
        {
            name: 'New',
            value: CLAIM_STAGES.NEW
        },
        {
            name: 'In Review',
            value: CLAIM_STAGES.IN_REVIEW
        },
        {
            name: 'Acknowledged',
            value: CLAIM_STAGES.ACKNOWLEDGED
        },
        {
            name: 'DV Issued',
            value: CLAIM_STAGES.DV_ISSUED
        },
        {
            name: 'DV Signed',
            value: CLAIM_STAGES.DV_SIGNED
        },
        {
            name: 'Adjusted',
            value: CLAIM_STAGES.ADJUSTED
        },
        {
            name: 'Assessed',
            value: CLAIM_STAGES.ASSESSED
        },
        {
            name: 'Paid',
            value: CLAIM_STAGES.PAID
        }
    ];

    function getDefaultFilters() {
        const filters: ITableFilterItem[] = [
            {
                name: 'stage',
                value: '',
                type: 'select',
                list: enums?.claimStages?.map((type: IEnum) => ({
                    name: removeUnderscoresAndCapitalize(type.title),
                    value: type.key,
                })),
                placeholder: 'Select stage',
                label: 'Stage'
            },
        ]

        if (!isMotorClaim) {
            filters?.push({
                name: 'policy_type',
                value: '',
                type: 'select',
                placeholder: 'Select policy type',
                label: capitalizeSentence('Policy type'),
                list: enums?.policyTypes?.map((type: IEnum) => ({
                    name: removeUnderscoresAndCapitalize(type.title),
                    value: type.key,
                })),
            })
        }
        return filters;
    }

    // NON AUTO CLAIMS
    const fetchNonAutoClaimByClaimNumber = async () => {
        if (!claimNumber) return null;
        return new Promise<Claim>((resolve, reject) => {
            getNonMotorClaimByClaimNumber(claimNumber)
                .then((response: any) => {
                    resolve(new Claim(response));
                })
                .catch((error: Error) => {
                    reject(error);
                });
        });
    };

    const handleuploadClaimDocs = () => {
        if (!claim?.claimNumber) return

        updateModalStates({
            show: true,
            title: 'Upload Claim Document',
            size: 'sm',
            children: (
                <UploadClaimDocument
                    claimNumber={claim?.claimNumber}
                    onProceed={invalidateClaim}
                    url={UPLOAD_IMAGE_ENDPOINTS.DOCUMENT}
                    queryKey=""
                    onClose={closeModal}
                    stage={claim?.claimStage}
                />
            )
        })
    }

    return {
        handleUpdateClaimStageToInReview,
        handleUpdateClaimStageToSubmitted,
        handleUpdateClaimStageToAcknowledged,
        handleUpdateClaimStageToAssessed,
        handleUpdateClaimStageToPaid,
        handleUpdateClaimStageToAdjusted,
        handleUpdateClaimStageToDvIssued,
        handleUpdateClaimStageToDvSigned,
        getInitFilters,
        fetchClaim,
        ClaimModal,
        exportClaim,
        columns,
        CLAIM_TABLE_FILTER_OPTIONS,
        createClaim,
        getDefaultFilters,
        fetchClaimStatistics,
        handleuploadClaimDocs,
        invalidateClaim,
        invalidategetAllClaimsList,
        // NON AUTO CLAIMS
        getAllClaimsList,
        fetchNonAutoClaimByClaimNumber,
        claimsPaginationData
    }
};
interface ICreateClaimSecondChild {
    claimNumber: string;
    isMotorClaim: boolean;
}

interface ICreateClaimConfirmationChild {
    policyNumber: string;
    isMotorClaim: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}